import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import vahoy from '../../../javascript/vahoy';
import AiDetectorModal from './ai_detector_modal';
import { authenticatedHeadersHash } from '../../../javascript/v2_authentication';
import withV2HealthCheck from '../../with_v2_health_check';
import stripHtmlToVisibleText from '../../../javascript/stripHtmlToVisibleText';

const AI_DETECTOR_REQUEST_PATHS_KEY = 'ai_detector_request_paths';
const AI_DETECTION_POLL_INTERVAL_MS = 2000;
const AI_DETECTION_POLL_TIMEOUT_MS = 30000;

function pollDeadline() {
  return Date.now() + AI_DETECTION_POLL_TIMEOUT_MS;
}

function pollSleep() {
  return new Promise((resolve) => { setTimeout(resolve, AI_DETECTION_POLL_INTERVAL_MS); });
}

// Reads a pitch's detection result from the V2 pitches API, polling until the background
// job has written it (or we give up). V1 sends the V1 pitch id, stored in V2 as migrate_id.
// A non-OK response (e.g. 401 from an expired token, 5xx) is a hard failure: throw at once
// rather than swallowing it and spinning until the timeout. `isMounted` lets the caller abort
// the loop early when the component unmounts mid-poll. Exported for unit testing.
async function pollForDetection(pitchId, isMounted = () => true) {
  const url = `${window.V2_SUPABASE_API_EXTERNAL_URL}/rest/v1/pitches?migrate_id=eq.${pitchId}&select=ai_generated_probability,ai_text_detection_analyzed_at`;
  const deadline = pollDeadline();
  while (Date.now() < deadline) {
    if (!isMounted()) return undefined;
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(url, { headers: authenticatedHeadersHash() });
    if (!response.ok) throw new Error(`AI detection lookup failed (${response.status})`);
    // eslint-disable-next-line no-await-in-loop
    const rows = await response.json();
    if (rows[0]?.ai_text_detection_analyzed_at != null) return rows[0].ai_generated_probability;
    // eslint-disable-next-line no-await-in-loop
    await pollSleep();
  }
  throw new Error('AI detection timed out');
}

// Reads a text-path detection result (no saved pitch to poll a PostgREST view for) from V2's
// own detection-status endpoint. 202 means still pending; any other non-OK response is a hard
// failure, same as pollForDetection. Same isMounted abort semantics. Exported for unit testing.
async function pollForTextDetection(detectionId, isMounted = () => true) {
  const url = `${window.V2_URL}/enrich/api/ai_text_detections/${detectionId}`;
  const deadline = pollDeadline();
  while (Date.now() < deadline) {
    if (!isMounted()) return undefined;
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(url, { headers: authenticatedHeadersHash() });
    if (response.status !== 202) {
      if (!response.ok) throw new Error(`AI detection lookup failed (${response.status})`);
      // eslint-disable-next-line no-await-in-loop
      const body = await response.json();
      return body.fraction_ai ?? body.avg_ai_likelihood;
    }
    // eslint-disable-next-line no-await-in-loop
    await pollSleep();
  }
  throw new Error('AI detection timed out');
}

export { pollForDetection, pollForTextDetection };

const getStoredRequestPaths = () => {
  if (typeof window === 'undefined') return [];
  try {
    const storedValue = window.localStorage.getItem(AI_DETECTOR_REQUEST_PATHS_KEY);
    if (!storedValue) return [];
    return JSON.parse(storedValue);
  } catch (error) {
    window.localStorage.removeItem(AI_DETECTOR_REQUEST_PATHS_KEY);
    return [];
  }
};

const persistRequestPath = (requestPath) => {
  if (typeof window === 'undefined' || !requestPath) return;

  try {
    const storedPaths = getStoredRequestPaths();
    if (!storedPaths.includes(requestPath)) {
      window.localStorage.setItem(AI_DETECTOR_REQUEST_PATHS_KEY, JSON.stringify([...storedPaths, requestPath]));
    }
  } catch (error) {
    window.localStorage.setItem(AI_DETECTOR_REQUEST_PATHS_KEY, JSON.stringify([requestPath]));
  }
};

function AiDetectorButton({
                            text, pitchId, ahoyObjectGid, displayAsButton, checkAiDetectorVisibility,
                          }) {
  // AI detection consumes VISIBLE text — rich (html) pitches would send markup to the vendor.
  const plainText = stripHtmlToVisibleText(text);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiResult, setAiResult] = useState({
    predictedClass: '',
    classProbability: { ai: 0, human: 0, mixed: 0 },
  });
  const [hideAiButton, setHideAiButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Tracks a detection request in flight. Distinct from `loading` (which starts true and drives
  // the modal's scanning bar) — this only gates the trigger button so it can't be re-fired.
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Guards against state updates after unmount: the poll below can stay in flight for up to
  // AI_DETECTION_POLL_TIMEOUT_MS, well after the user closes the modal or navigates away.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  // Non-button mode renders as an inline link (V2's variant="link" look) — color/hover
  // come from .ai-detector-link so the theme tokens apply.
  const buttonClassName = displayAsButton
    ? 'btn ai-detector-button'
    : 'ai-detector-link';

  const buttonStyle = !displayAsButton
    ? {
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        cursor: 'pointer',
      }
    : {};

  useEffect(() => {
    if (!checkAiDetectorVisibility || typeof window === 'undefined') {
      setHideAiButton(false);
      return;
    }

    const storedPaths = getStoredRequestPaths();
    setHideAiButton(storedPaths.includes(window.location.pathname));
  }, [checkAiDetectorVisibility]);

  const handleClick = async () => {
    if (isAnalyzing) return; // guard against double-fires while a request is in flight
    setShowModal(true);
    setLoading(true);
    setIsAnalyzing(true);
    setErrorMessage('');

    vahoy.track('AiDetectorButton#showModal', { text: plainText, object_gid: ahoyObjectGid });

    try {
      const apiUrl = `${window.V2_URL}/enrich/api/ai_text_detection?service=pangram${typeof pitchId !== 'undefined' ? `&migrate_id=${pitchId}` : ''}`;
      const headers = {
        ...authenticatedHeadersHash(),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      // POST triggers detection: 200 with the result on a cache hit, 202 when enqueued.
      // On 202 the result is read from the pitches API once the job has written it.
      const response = await fetch(apiUrl, { method: 'POST', headers, body: JSON.stringify({ text: plainText }) });

      let aiLikelihood;
      if (response.status === 202) {
        const acceptedBody = await response.json();
        if (typeof pitchId !== 'undefined') {
          aiLikelihood = await pollForDetection(pitchId, () => isMountedRef.current);
        } else if (acceptedBody.detection_id) {
          aiLikelihood = await pollForTextDetection(acceptedBody.detection_id, () => isMountedRef.current);
        } else {
          throw new Error('Failed to analyze text');
        }
        if (!isMountedRef.current) return; // unmounted mid-poll — drop the result
      } else if (response.ok) {
        const data = await response.json();
        aiLikelihood = data.avg_ai_likelihood ?? data.fraction_ai;
      } else {
        throw new Error('Failed to analyze text');
      }

      const predictedClass = aiLikelihood > 0.5 ? 'ai' : 'human';
      const classProbability = {
                                ai: aiLikelihood,
                                human: 1 - aiLikelihood,
                              };

      setAiResult({
        predictedClass,
        classProbability,
      });

      if (checkAiDetectorVisibility && typeof window !== 'undefined') {
        persistRequestPath(window.location.pathname);
        setHideAiButton(true);
      }
    } catch (error) {
      if (!isMountedRef.current) return; // unmounted while the request was in flight
      setAiResult({
        predictedClass: '',
        classProbability: { ai: 0, human: 0 },
      });
      setErrorMessage("We couldn't check this pitch right now. Please try again in a moment.");
    } finally {
      if (isMountedRef.current) {
        setLoading(false); // Set loading to false after the API call finishes
        setIsAnalyzing(false);
      }
    }
  };

  const handleClose = () => {
    if (!text) return;
    setShowModal(false);
    setAiResult({
      predictedClass: '',
      classProbability: { ai: 0, human: 0 },
    });
    setErrorMessage('');
    setLoading(true); // Reset loading for the next API call
  };

  return (
    <>
      {(!checkAiDetectorVisibility || !hideAiButton) && (
        <button
          className={buttonClassName}
          onClick={handleClick}
          type="button"
          disabled={(displayAsButton && !text) || isAnalyzing}
          style={buttonStyle}
        >
          <i className="fa-solid fa-sparkles" /> Check for AI
        </button>
      )}
      <AiDetectorModal
        text={plainText}
        showModal={showModal}
        onClose={handleClose}
        aiResult={aiResult}
        loading={loading}
        errorMessage={errorMessage}
        ahoyObjectGid={ahoyObjectGid}
      />
    </>
  );
}

AiDetectorButton.propTypes = {
  text: PropTypes.string,
  pitchId: PropTypes.number,
  ahoyObjectGid: PropTypes.string,
  displayAsButton: PropTypes.bool,
  checkAiDetectorVisibility: PropTypes.bool,
};

AiDetectorButton.defaultProps = {
  text: undefined,
  pitchId: undefined,
  ahoyObjectGid: undefined,
  displayAsButton: true,
  checkAiDetectorVisibility: false,
};

export default withV2HealthCheck(AiDetectorButton, null);
